/**
 * Puerta de validación servidor para exportación de conversaciones (PDF/HTML en cliente).
 * Usa PlanEnforcer allow_export; el frontend no debe confiar solo en isPro.
 */

import { apiService } from './api';

/**
 * Regla única alineada con PlanStatusContext: unlimited OR export_enabled booleano real.
 *
 * @param {object|null|undefined} plan — planStatus.plan
 * @returns {boolean}
 */
export function canExportFromPlan(plan) {
  if (!plan || typeof plan !== 'object') return false;
  if (plan.is_unlimited_user === true) return true;
  return plan.export_enabled === true;
}

/**
 * @param {unknown} err
 * @returns {boolean}
 */
export function isPlanRestrictionExportError(err) {
  if (!err || typeof err !== 'object') return false;
  if (err.status !== 403) return false;
  const data = err.data;
  if (data && typeof data === 'object' && data.error === 'plan_restriction') return true;
  return err.message === 'plan_restriction';
}

/**
 * Validación servidor sin throws: siempre { ok, error? }.
 *
 * @returns {Promise<{ ok: true } | { ok: false, error: string, planRestriction?: boolean, httpStatus?: number }>}
 */
export async function checkConversationExportAllowed() {
  try {
    const data = await apiService.request('/export_conversation.php', {
      method: 'GET',
      _skipDedupe: true,
      _skipGlobalErrorModal: true,
    });
    if (data && data.success === true) {
      return { ok: true };
    }
    return {
      ok: false,
      error: data?.error || 'export_gate_failed',
    };
  } catch (e) {
    if (isPlanRestrictionExportError(e)) {
      return { ok: false, error: 'PLAN_RESTRICTION_EXPORT', planRestriction: true };
    }
    const msg = e && typeof e === 'object' ? (e.message || '') : String(e);
    const status = e?.status;
    if (
      msg === 'Invalid JSON response'
      || (typeof msg === 'string' && msg.includes('Invalid JSON'))
      || e?.code === 'NETWORK_ERROR'
    ) {
      return { ok: false, error: 'EXPORT_GATE_UNAVAILABLE' };
    }
    if (typeof status === 'number' && status >= 400) {
      return {
        ok: false,
        error: e?.data?.error || msg || `HTTP_${status}`,
        httpStatus: status,
      };
    }
    return { ok: false, error: msg || 'EXPORT_GATE_ERROR' };
  }
}

/**
 * Compatibilidad: delega en checkConversationExportAllowed y lanza errores legacy.
 * @returns {Promise<void>}
 * @throws {Error} Con planRestrictionExport=true si 403 plan_restriction; otros errores de red/API tal cual.
 */
export async function assertConversationExportAllowed() {
  const r = await checkConversationExportAllowed();
  if (r.ok) return;
  if (r.planRestriction) {
    const out = new Error('PLAN_RESTRICTION_EXPORT');
    out.planRestrictionExport = true;
    throw out;
  }
  const err = new Error(r.error || 'export_gate_failed');
  if (r.error === 'EXPORT_GATE_UNAVAILABLE') {
    err.exportGateServerError = true;
  }
  throw err;
}
